/**
 * Workout Builder Page (Admin) - Epic 3
 * Visual constructor for workout templates
 */
import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  ArrowLeft,
  Plus,
  Save,
  Trash2,
  GripVertical,
  Dumbbell,
  Clock,
  Loader2,
  Link2,
  ChevronDown,
  ChevronUp,
  Copy,
  X,
} from "lucide-react";
import {
  useExercises,
  useWorkoutTemplate,
  createWorkoutTemplate,
  updateWorkoutTemplate,
} from "../hooks/useWorkout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Separator } from "../components/ui/separator";
import { Toaster, toast } from "sonner";

const SUPERSET_GROUPS = ["A", "B", "C", "D", "E"];

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 }
};

export default function WorkoutBuilderPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditing = !!templateId;
  
  const { exercises, isLoading: exercisesLoading } = useExercises();
  const { template, isLoading: templateLoading, mutate: mutateTemplate } = useWorkoutTemplate(templateId);
  
  const [saving, setSaving] = useState(false);
  const [expandedExercises, setExpandedExercises] = useState({});
  
  // Form setup with react-hook-form
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      exercises: [],
    },
    values: template ? {
      name: template.name,
      description: template.description || "",
      exercises: template.template_exercises?.map((te) => ({
        id: te.id,
        exercise_id: te.exercise_id,
        order_index: te.order_index,
        rest_seconds: te.rest_seconds,
        coach_notes: te.coach_notes || "",
        superset_group: te.superset_group || "",
        sets: te.sets?.map((s) => ({
          set_number: s.set_number,
          target_reps: s.target_reps,
          target_weight_kg: s.target_weight_kg || "",
        })) || [],
      })) || [],
    } : undefined,
  });
  
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "exercises",
  });
  
  const watchedExercises = watch("exercises");
  
  // Toggle exercise expansion
  const toggleExpand = (index) => {
    setExpandedExercises((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  
  // Add new exercise to template
  const handleAddExercise = () => {
    append({
      exercise_id: "",
      order_index: fields.length,
      rest_seconds: 90,
      coach_notes: "",
      superset_group: "",
      sets: [
        { set_number: 1, target_reps: 10, target_weight_kg: "" },
      ],
    });
    // Auto-expand new exercise
    setExpandedExercises((prev) => ({
      ...prev,
      [fields.length]: true,
    }));
  };
  
  // Add set to an exercise
  const handleAddSet = (exerciseIndex) => {
    const currentSets = watchedExercises[exerciseIndex]?.sets || [];
    const newSetNumber = currentSets.length + 1;
    
    const updatedSets = [
      ...currentSets,
      { set_number: newSetNumber, target_reps: 10, target_weight_kg: "" },
    ];
    
    setValue(`exercises.${exerciseIndex}.sets`, updatedSets);
  };
  
  // Remove set from an exercise
  const handleRemoveSet = (exerciseIndex, setIndex) => {
    const currentSets = watchedExercises[exerciseIndex]?.sets || [];
    const updatedSets = currentSets
      .filter((_, i) => i !== setIndex)
      .map((s, i) => ({ ...s, set_number: i + 1 }));
    
    setValue(`exercises.${exerciseIndex}.sets`, updatedSets);
  };
  
  // Duplicate an exercise
  const handleDuplicateExercise = (index) => {
    const exercise = watchedExercises[index];
    append({
      ...exercise,
      id: undefined,
      order_index: fields.length,
    });
  };
  
  // Get exercise name by ID
  const getExerciseName = (exerciseId) => {
    const ex = exercises.find((e) => e.id === exerciseId);
    return ex?.name || "Sélectionner...";
  };
  
  // Submit form
  const onSubmit = async (data) => {
    if (data.exercises.length === 0) {
      toast.error("Ajoutez au moins un exercice");
      return;
    }
    
    // Validate all exercises have an exercise_id
    for (let i = 0; i < data.exercises.length; i++) {
      if (!data.exercises[i].exercise_id) {
        toast.error(`Sélectionnez un exercice pour la position ${i + 1}`);
        return;
      }
      if (data.exercises[i].sets.length === 0) {
        toast.error(`Ajoutez au moins une série à l'exercice ${i + 1}`);
        return;
      }
    }
    
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        description: data.description || null,
        exercises: data.exercises.map((ex, idx) => ({
          exercise_id: ex.exercise_id,
          order_index: idx,
          rest_seconds: parseInt(ex.rest_seconds) || 90,
          coach_notes: ex.coach_notes || null,
          superset_group: ex.superset_group || null,
          sets: ex.sets.map((s, sIdx) => ({
            set_number: sIdx + 1,
            target_reps: parseInt(s.target_reps) || 10,
            target_weight_kg: s.target_weight_kg ? parseFloat(s.target_weight_kg) : null,
          })),
        })),
      };
      
      if (isEditing) {
        await updateWorkoutTemplate(templateId, {
          name: payload.name,
          description: payload.description,
        });
        toast.success("Programme mis à jour");
      } else {
        await createWorkoutTemplate(payload);
        toast.success("Programme créé");
      }
      
      navigate("/admin/templates");
    } catch (err) {
      toast.error(err.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  if (templateLoading || (isEditing && !template)) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 pb-24">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/admin/templates")}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <h1 className="font-heading text-xl font-bold text-white">
                {isEditing ? "MODIFIER LE PROGRAMME" : "CRÉER UN PROGRAMME"}
              </h1>
            </div>
            
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={saving}
              data-testid="save-template-button"
              className="h-10 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form className="space-y-8">
          {/* Template Info */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-white">
                Informations du programme
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Nom du programme *</Label>
                <Input
                  {...register("name", { required: "Nom requis" })}
                  placeholder="Push Day A"
                  data-testid="template-name-input"
                  className="bg-zinc-950 border-zinc-800 h-11"
                />
                {errors.name && (
                  <p className="text-red-400 text-sm">{errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Description</Label>
                <Textarea
                  {...register("description")}
                  placeholder="Séance axée sur les pectoraux et les épaules..."
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Exercises List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-white">
                EXERCICES ({fields.length})
              </h2>
              <Button
                type="button"
                onClick={handleAddExercise}
                data-testid="add-exercise-to-template"
                className="h-10 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            
            {fields.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
                <CardContent className="py-12 text-center">
                  <Dumbbell className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 mb-2">Aucun exercice</p>
                  <p className="text-zinc-600 text-sm">
                    Cliquez sur "Ajouter" pour construire votre programme
                  </p>
                </CardContent>
              </Card>
            ) : (
              <AnimatePresence mode="popLayout">
                {fields.map((field, index) => {
                  const isExpanded = expandedExercises[index] ?? true;
                  const currentExercise = watchedExercises[index];
                  const supersetGroup = currentExercise?.superset_group;
                  
                  return (
                    <motion.div
                      key={field.id}
                      variants={itemVariants}
                      initial="hidden"
                      animate="show"
                      exit="exit"
                      layout
                    >
                      <Card
                        className={`bg-zinc-900/50 border-zinc-800 ${
                          supersetGroup ? `border-l-4 border-l-sky-400` : ""
                        }`}
                        data-testid={`exercise-item-${index}`}
                      >
                        <Collapsible open={isExpanded} onOpenChange={() => toggleExpand(index)}>
                          {/* Header */}
                          <div className="flex items-center gap-3 p-4">
                            <div className="text-zinc-500 cursor-grab">
                              <GripVertical className="w-5 h-5" />
                            </div>
                            
                            <div className="w-8 h-8 bg-lime-400/10 rounded-lg flex items-center justify-center">
                              <span className="text-lime-400 font-bold text-sm">
                                {index + 1}
                              </span>
                            </div>
                            
                            <div className="flex-1">
                              <Controller
                                name={`exercises.${index}.exercise_id`}
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    value={field.value}
                                    onValueChange={field.onChange}
                                  >
                                    <SelectTrigger
                                      className="bg-zinc-950 border-zinc-800 h-10 w-full"
                                      data-testid={`select-exercise-${index}`}
                                    >
                                      <SelectValue placeholder="Sélectionner un exercice" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                                      {exercises.map((ex) => (
                                        <SelectItem key={ex.id} value={ex.id}>
                                          {ex.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                            </div>
                            
                            {supersetGroup && (
                              <Badge className="bg-sky-400/10 text-sky-400 border-sky-400/30">
                                <Link2 className="w-3 h-3 mr-1" />
                                Superset {supersetGroup}
                              </Badge>
                            )}
                            
                            <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                              {currentExercise?.sets?.length || 0} séries
                            </Badge>
                            
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-zinc-400">
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5" />
                                ) : (
                                  <ChevronDown className="w-5 h-5" />
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDuplicateExercise(index)}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => remove(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <CollapsibleContent>
                            <Separator className="bg-zinc-800" />
                            
                            <div className="p-4 space-y-4">
                              {/* Settings Row */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-zinc-400 text-xs">Repos (sec)</Label>
                                  <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                    <Input
                                      type="number"
                                      {...register(`exercises.${index}.rest_seconds`)}
                                      className="pl-10 bg-zinc-950 border-zinc-800 h-10"
                                    />
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <Label className="text-zinc-400 text-xs">Superset</Label>
                                  <Controller
                                    name={`exercises.${index}.superset_group`}
                                    control={control}
                                    render={({ field }) => (
                                      <Select
                                        value={field.value || "none"}
                                        onValueChange={(v) => field.onChange(v === "none" ? "" : v)}
                                      >
                                        <SelectTrigger
                                          className="bg-zinc-950 border-zinc-800 h-10"
                                          data-testid={`superset-${index}`}
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                          <SelectItem value="none">Aucun</SelectItem>
                                          {SUPERSET_GROUPS.map((g) => (
                                            <SelectItem key={g} value={g}>
                                              Groupe {g}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                  />
                                </div>
                                
                                <div className="col-span-2 space-y-2">
                                  <Label className="text-zinc-400 text-xs">Notes coach</Label>
                                  <Input
                                    {...register(`exercises.${index}.coach_notes`)}
                                    placeholder="Instructions..."
                                    className="bg-zinc-950 border-zinc-800 h-10"
                                  />
                                </div>
                              </div>
                              
                              {/* Sets Table */}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                                    Séries
                                  </Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleAddSet(index)}
                                    className="text-lime-400 hover:text-lime-300 h-8"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Série
                                  </Button>
                                </div>
                                
                                <div className="bg-zinc-950 rounded-lg border border-zinc-800 overflow-hidden">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b border-zinc-800">
                                        <th className="px-3 py-2 text-left text-xs text-zinc-500 font-medium">
                                          #
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs text-zinc-500 font-medium">
                                          Reps cibles
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs text-zinc-500 font-medium">
                                          Charge (kg)
                                        </th>
                                        <th className="w-10"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {currentExercise?.sets?.map((set, setIndex) => (
                                        <tr
                                          key={setIndex}
                                          className="border-b border-zinc-800/50 last:border-0"
                                        >
                                          <td className="px-3 py-2">
                                            <span className="text-zinc-500 text-sm">
                                              {setIndex + 1}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2">
                                            <Input
                                              type="number"
                                              {...register(`exercises.${index}.sets.${setIndex}.target_reps`)}
                                              className="bg-zinc-900 border-zinc-700 h-8 w-20"
                                              data-testid={`set-reps-${index}-${setIndex}`}
                                            />
                                          </td>
                                          <td className="px-3 py-2">
                                            <Input
                                              type="number"
                                              step="0.5"
                                              {...register(`exercises.${index}.sets.${setIndex}.target_weight_kg`)}
                                              placeholder="-"
                                              className="bg-zinc-900 border-zinc-700 h-8 w-24"
                                              data-testid={`set-weight-${index}-${setIndex}`}
                                            />
                                          </td>
                                          <td className="px-2 py-2">
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => handleRemoveSet(index, setIndex)}
                                              className="text-zinc-500 hover:text-red-400 h-8 w-8"
                                              disabled={currentExercise?.sets?.length <= 1}
                                            >
                                              <X className="w-4 h-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
